/**
 * Display headings that live in the narrow left rail of a service section.
 *
 * `ServiceAbout`, `QuickAnswers` and `ServiceFaq` all set a CMS heading at
 * display size inside the same `minmax(320px,420px)` grid column. A single
 * word longer than about ten glyphs does not fit that column at those sizes,
 * and a word has no break opportunity inside it: the glyphs render past the
 * column and paint over the content beside them. On
 * /shopify-experts/shopify-development, "development" measured 496px against a
 * 420px column in the About heading (76px of overspill, straight through the
 * quick-summary card) and 552px in Quick answers (132px, over the accordion).
 *
 * `overflow-wrap: break-word` would contain it, and is the wrong tool here:
 * an unhyphenated mid-word break in a 98px display face reads as a typo, and
 * CSS hyphenation is not reliable enough across browsers to supply the hyphen.
 * So the heading steps down instead, to a scale whose longest word fits.
 *
 * `ServiceFaq` worked this out first and carried the rule inline. It lives
 * here now so the three sections cannot drift apart — the FAQ heading and the
 * About heading two screens above it stepping down at different thresholds
 * would be worse than either rule on its own.
 */

/**
 * Glyph count past which a word overspills the rail at display sizes.
 * "Optimization" (12) and "development" (11) overflow; "migration" (9) does
 * not. Ten is the last length that fits with room to spare.
 */
const LONG_WORD_GLYPHS = 10;

/** True when any word in the heading is too long for the rail column. */
export function hasLongHeadingWord(
  ...parts: Array<string | undefined | null>
): boolean {
  return parts
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .split(/\s+/)
    .some((word) => word.length > LONG_WORD_GLYPHS);
}

/**
 * Tailwind size classes for a rail heading, picked from the heading's own
 * text. `scale` names the section's normal top size so the three sections
 * keep the sizes they were designed at rather than being levelled.
 *
 * THE LEADING IS RETURNED WITH THE SIZES, AND LAST, ON PURPOSE. `cn()` is
 * tailwind-merge, and tailwind-merge follows Tailwind in treating a font size
 * as something that also sets a line height — so a `text-<size>` silently
 * deletes any `leading-*` that came before it:
 *
 *   twMerge('leading-[0.95]', 'text-[40px]')  ->  'text-[40px]'
 *   twMerge('text-[40px]', 'leading-[0.95]')  ->  'text-[40px] leading-[0.95]'
 *
 * All three sections used to write `cn('… leading-… …', sizes)`, which is the
 * first form. The class vanished from the rendered markup and the headings
 * fell back to normal line height — visible as three loose lines where the
 * design sets 0.95, and invisible in the source, because the class is right
 * there in the file. Returning it here keeps the order correct in one place
 * instead of in every caller. `rail-heading.test.ts` fails if it goes missing
 * again.
 */
export function railHeadingSize(
  scale: 'xl88' | 'xl98',
  long: boolean,
  /** The section's designed line height. Appended after the sizes. */
  leading = 'leading-[0.95]',
): string {
  const sizes =
    scale === 'xl88'
      ? long
        ? 'text-[40px] md:text-[52px] lg:text-[56px] xl:text-[60px]'
        : 'text-[40px] md:text-[60px] lg:text-[72px] xl:text-[88px] tracking-[-0.022em] md:tracking-[-0.027em] lg:tracking-[-0.029em] xl:tracking-[-0.031em]'
      : long
        ? 'text-[44px] md:text-[56px] lg:text-[60px] xl:text-[64px]'
        : 'text-[48px] md:text-[68px] lg:text-[80px] xl:text-[98px] tracking-[-0.024em] md:tracking-[-0.029em] lg:tracking-[-0.03em] xl:tracking-[-0.032em]';
  return `${sizes} ${leading}`;
}
